import { useQuery } from '@tanstack/react-query';
import { backendServices } from '../services';
import { useMemo } from 'react';

/**
 * Represents a dropdown option for list of buyer companies.
 *
 * @property label - The display name of the company in the format "Company Name [Company ID]".
 * @property value - The unique identifier of the company as a string.
 */
export interface BuyerCompanyOption {
    label: string;
    value: string;
}

/**
 * Custom hook to fetch all buyer companies.
 */
export const useGetAllBuyerCompanies = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const { isLoading, error, data, isFetching, status } = useQuery(
        ['BuyerCompanies'],
        async () => {
            const api = new backendServices.Apis.CompanyApi();

            const res = await api.getCompanies({ isBuyer: true });

            return res.data;
        },
        {
            enabled: enabled,
            staleTime: 60 * 60 * 1000,
            cacheTime: 60 * 60 * 1000,
            retry: 0,
        }
    );

    return {
        isLoading: isLoading || isFetching,
        error,
        data,
        status,
    };
};

/**
 * Custom hook to transform a list of buyer companies into an array of options.
 *
 * @param {Object} params - The parameters object.
 * @param {backendServices.ViewModels.CompanyViewModel[]} params.buyerCompanies - Array of buyer company view models.
 * @returns {BuyerCompanyOption[]} Array of buyer company options.
 */
export const useGetAllBuyerCompanyOptions = ({
    buyerCompanies,
}: {
    buyerCompanies: backendServices.ViewModels.CompanyViewModel[];
}) => {
    const buyerCompanyOptions: BuyerCompanyOption[] = useMemo(() => {
        return buyerCompanies.map((company) => ({
            label: company.Name + ` [${[company.ID.toString()]}]`,
            value: company.ID.toString(),
        }));
    }, [buyerCompanies]);

    return buyerCompanyOptions;
};
